import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Trash2 } from "lucide-react";
import { Modal } from "@/components/Modal";

/** The 409 `details` the API returns when a record still has dependants. */
export interface Dependants {
  entity: "Unit" | "StaffMember" | string;
  label: string;
  canForce?: boolean;
  fees?: number;
  payments?: number;
  permits?: number;
  users?: number;
  maintenanceRequests?: number;
  keys?: number;
  tasks?: number;
  timeEntries?: number;
}

/** True when an axios error is the API's "has dependants, may be forced" refusal. */
export function isForceable(err: unknown): Dependants | null {
  const data = (err as any)?.response?.data;
  return data?.code === "HAS_DEPENDANTS" && data?.details?.canForce ? (data.details as Dependants) : null;
}

/**
 * Second, explicit confirmation before an admin deletes a record together with
 * everything that depends on it. The first confirm (AdminRecordActions) covers the
 * record itself; this one spells out the associated data that will go with it and
 * requires the admin to acknowledge that before the red button enables.
 */
export function ForceDeleteDialog({
  open,
  dependants,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  dependants: Dependants | null;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const tr = (es: string, en: string) => (isEn ? en : es);
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    if (open) setAcknowledged(false);
  }, [open]);

  if (!open || !dependants) return null;
  const d = dependants;
  const entityLabel = d.entity === "Unit" ? tr(`la unidad ${d.label}`, `unit ${d.label}`) : d.entity === "StaffMember" ? tr(`a ${d.label}`, d.label) : d.label;

  const willDelete: string[] = [];
  const willUnlink: string[] = [];
  const n = (count: number | undefined, es: [string, string], en: [string, string]) =>
    count === undefined ? null : `${count} ${isEn ? (count === 1 ? en[0] : en[1]) : count === 1 ? es[0] : es[1]}`;
  if (d.fees) willDelete.push(`${n(d.fees, ["cuota de mantenimiento", "cuotas de mantenimiento"], ["maintenance fee", "maintenance fees"])}${d.payments ? tr(` (con ${d.payments} pago(s) registrado(s))`, ` (with ${d.payments} recorded payment(s))`) : ""}`);
  if (d.permits) willDelete.push(n(d.permits, ["permiso de obra", "permisos de obra"], ["works permit", "works permits"])!);
  if (d.tasks) willDelete.push(n(d.tasks, ["tarea asignada", "tareas asignadas"], ["assigned task", "assigned tasks"])!);
  if (d.timeEntries) willDelete.push(n(d.timeEntries, ["entrada de tiempo", "entradas de tiempo"], ["time entry", "time entries"])!);
  if (d.users) willUnlink.push(n(d.users, ["usuario quedará sin unidad", "usuarios quedarán sin unidad"], ["user will be left without a unit", "users will be left without a unit"])!);
  if (d.maintenanceRequests) willUnlink.push(n(d.maintenanceRequests, ["solicitud de mantenimiento quedará sin unidad", "solicitudes de mantenimiento quedarán sin unidad"], ["maintenance request will lose its unit", "maintenance requests will lose their unit"])!);
  if (d.keys) willUnlink.push(n(d.keys, ["llave quedará sin unidad", "llaves quedarán sin unidad"], ["key will lose its unit", "keys will lose their unit"])!);

  return (
    <Modal open onClose={onCancel} title={tr("Confirmación adicional", "Additional confirmation")}>
      <div className="space-y-4">
        <div className="flex gap-3 rounded border-2 border-red-600 bg-red-50 p-4 text-red-800">
          <AlertTriangle size={28} className="shrink-0 text-red-600" />
          <div className="space-y-2 text-sm">
            <p className="font-semibold text-red-700">
              {tr(
                `Va a eliminar ${entityLabel} y TODOS sus registros asociados. Esta acción no se puede deshacer.`,
                `You are about to delete ${entityLabel} and ALL its associated records. This cannot be undone.`
              )}
            </p>
            {willDelete.length > 0 && (
              <div>
                <p className="font-medium">{tr("Se eliminarán definitivamente:", "Will be permanently deleted:")}</p>
                <ul className="list-disc pl-5">
                  {willDelete.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
            {willUnlink.length > 0 && (
              <div>
                <p className="font-medium">{tr("Además:", "Also:")}</p>
                <ul className="list-disc pl-5">
                  {willUnlink.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
            )}
            <p className="text-xs">
              {tr(
                "Si estos registros pudieran ser datos reales (cuotas cobradas, permisos vigentes, horas trabajadas), cancele y revíselos antes.",
                "If any of these could be real data (collected fees, valid permits, worked hours), cancel and review them first."
              )}
            </p>
          </div>
        </div>
        <label className="flex items-start gap-2 text-sm text-slate-800">
          <input type="checkbox" className="mt-0.5" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} />
          <span>
            {tr(
              "Entiendo que se eliminarán definitivamente el registro y sus datos asociados.",
              "I understand the record and its associated data will be permanently deleted."
            )}
          </span>
        </label>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-sm border rounded border-slate-300 hover:bg-slate-50">
            {tr("Cancelar", "Cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!acknowledged || pending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          >
            <Trash2 size={14} /> {pending ? tr("Eliminando…", "Deleting…") : tr("Eliminar definitivamente", "Delete permanently")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
